import { useCallback, useEffect, useRef } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';

// How often to re-check while the tab stays open. Combined with the focus /
// visibility checks below, a returning user is prompted promptly without
// polling aggressively.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const SNACKBAR_KEY = 'app-update-available';

// Base-aware, so it resolves correctly under a non-root VITE_BASENAME.
const versionUrl = `${import.meta.env.BASE_URL}version.json`;

/**
 * Proactively detects a redeploy on a long-lived tab and invites the user to
 * refresh — the complement to the *reactive* recovery (lazyWithReload + the
 * `vite:preloadError` handler in main.tsx), which only kicks in once a stale
 * chunk is actually requested.
 *
 * It polls the per-build `/version.json` (emitted by vite.config) on an
 * interval and whenever the tab regains focus/visibility, comparing every
 * response against the first one this tab saw (its boot version). On a mismatch
 * it shows a single persistent "new version available — Refresh" snackbar.
 *
 * No-ops in dev (no version.json is emitted) and fails silently while offline.
 */
export function useAppUpdatePrompt() {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  // The version this tab booted with — captured on the first successful fetch.
  const baselineRef = useRef<string | null>(null);
  // Prompt at most once per page load, even if several checks race.
  const notifiedRef = useRef(false);

  const promptUpdate = useCallback(() => {
    enqueueSnackbar(
      // Vertical layout: the message wraps naturally on top, with a clear
      // Refresh CTA below it. Keeps the prompt tidy inside the (narrow) filled
      // Alert message column instead of floating the action beside wrapped text.
      <Stack direction="column" spacing={1} sx={{ py: 0.25 }}>
        <Box component="span" sx={{ lineHeight: 1.4 }}>
          {t('common.app_update.message')}
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => window.location.reload()}
          startIcon={<IconifyIcon icon="material-symbols:refresh-rounded" sx={{ fontSize: 18 }} />}
          sx={{
            alignSelf: 'flex-start',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            borderColor: 'rgba(255, 255, 255, 0.6)',
            '&:hover': {
              borderColor: 'common.white',
              bgcolor: 'rgba(255, 255, 255, 0.14)',
            },
          }}
        >
          {t('common.app_update.refresh')}
        </Button>
      </Stack>,
      { variant: 'info', persist: true, preventDuplicate: true, key: SNACKBAR_KEY },
    );
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    let cancelled = false;

    const check = async () => {
      if (notifiedRef.current || document.visibilityState === 'hidden') return;
      try {
        const res = await fetch(`${versionUrl}?t=${Date.now()}`, { cache: 'no-store' });
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        const buildId = data?.buildId;
        if (cancelled || !buildId) return;

        if (baselineRef.current === null) {
          baselineRef.current = buildId;
        } else if (buildId !== baselineRef.current) {
          notifiedRef.current = true;
          promptUpdate();
        }
      } catch {
        // Offline or transient network error — ignore and retry next tick.
      }
    };

    const handleVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', check);
    check();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', check);
    };
  }, [promptUpdate]);
}
